import { Button } from "@/components/flexkit/Button";
import { Text } from "@/components/podkit/typography/Text";
import {
    Agents,
    useCreateAgentExecution,
    useSendToAgentExecution,
    type PlainAgentExecution,
} from "@/queries/agent-queries";
import { ErrorMessage } from "@/components/ErrorMessage";
import {
    AgentCodeContext,
    SendToAgentExecutionRequest,
    UserInputBlock,
    UserInputBlock_TextInput,
} from "gitpod-next-api/gitpod/v1/agent_pb";
import { ChatBubble } from "@/components/ai/chat/ChatBubble";
import type { Message, UserMessage } from "@/hooks/use-stream-conversation";
import { EnvironmentOnboardingActions } from "@/components/environments/onboarding/EnvironmentOnboardingActions";
import { findMessageWithActions } from "./actions-for-message";
import { FragmentFileModification } from "@/components/ai/FragmentFileModification";
import { useEffect, useMemo, useState } from "react";
import { ExternalLink } from "@/components/podkit/typography/Link";

// eslint-disable-next-line react-refresh/only-export-components
export function generateStartSetupMessage(): string {
    return "Please help me set up the development environment for this repository";
}

// eslint-disable-next-line react-refresh/only-export-components
export function isStartSetupMessage(message: UserMessage): boolean {
    const content = message.fragments.map((fragment) => fragment.content).join(" ");
    return content === generateStartSetupMessage();
}

export type ConversationMessageStartSetupProps = {
    isAgentExecutionDone: boolean;
    agentExecution?: PlainAgentExecution;
    environmentId: string;
    messages: Message[];
};

export const ConversationMessageStartSetup = ({
    isAgentExecutionDone,
    agentExecution,
    environmentId,
    messages,
}: ConversationMessageStartSetupProps) => {
    const startAgentExecution = useCreateAgentExecution();
    const sendToAgentExecution = useSendToAgentExecution();

    const { userMessage, actions, edits, hasMoreUserMessages } = useMemo(
        () => findMessageWithActions(messages, isStartSetupMessage),
        [messages],
    );

    const [hasSentMessage, setHasSentMessage] = useState<boolean>(false);
    useEffect(() => {
        if (userMessage && !hasSentMessage) {
            setHasSentMessage(true);
        }
    }, [hasSentMessage, userMessage]);

    const startAIAssistedOnboarding = async () => {
        let agentExecutionId = agentExecution?.id;
        if (!agentExecutionId) {
            const { agentExecutionId: newAgentExecutionId } = await startAgentExecution.mutateAsync({
                agentId: Agents.DevelopmentEnvironmentSetup.id,
                codeContext: new AgentCodeContext({
                    context: {
                        case: "environmentId",
                        value: environmentId,
                    },
                }),
            });
            agentExecutionId = newAgentExecutionId;
        }
        await sendToAgentExecution.mutateAsync(
            new SendToAgentExecutionRequest({
                agentExecutionId: agentExecutionId,
                userInput: new UserInputBlock({
                    input: {
                        case: "text",
                        value: new UserInputBlock_TextInput({
                            content: generateStartSetupMessage(),
                        }),
                    },
                }),
            }),
        );
        setHasSentMessage(true);
    };

    const hasClickedButton = hasSentMessage || startAgentExecution.isPending || sendToAgentExecution.isPending;

    return (
        <div data-testid="conversation-message-start-setup" className="contents">
            <IntroductionMessage />
            {!userMessage && (
                <div className="flex flex-wrap gap-2">
                    <Button
                        disabled={hasClickedButton}
                        onClick={startAIAssistedOnboarding}
                        className="truncate"
                        data-testid="conversation-message-start-setup-button"
                    >
                        Yes Please
                    </Button>
                    <ErrorMessage error={startAgentExecution.error} />
                    <ErrorMessage error={sendToAgentExecution.error} />
                </div>
            )}
            {userMessage && agentExecution && (
                <ChatBubble message={userMessage} agentExecution={agentExecution} isLast={true} />
            )}
            {agentExecution && actions.length > 0 && (
                <EnvironmentOnboardingActions
                    // We're sharing the same agent execution, so we also consider the agent execution done
                    // if there are user messages after the one generated by this step.
                    isAgentExecutionDone={isAgentExecutionDone || hasMoreUserMessages}
                    agentExecution={agentExecution}
                    actions={actions}
                    title={{
                        completed: "Configured environment",
                        inProgress: "Configuring environment",
                    }}
                />
            )}
            {Object.entries(edits).map(([filePath, edits]) => (
                <div key={filePath} className="flex flex-col gap-1">
                    <Text className="truncate text-sm font-bold">Updated {filePath.split("/").pop()}</Text>
                    {edits.map((edit) => (
                        <FragmentFileModification
                            key={`${edit.id}-${edit.actionId}`}
                            fragment={edit}
                            defaultOpen={false}
                        />
                    ))}
                </div>
            ))}
        </div>
    );
};

const IntroductionMessage = () => {
    return (
        <>
            <Text className="text-base">
                It looks like this repository doesn&#x2019;t have a configured development environment. Do you want me
                to review the repository and set up{" "}
                <ExternalLink href="https://www.gitpod.io/docs/gitpod/introduction/devcontainer">
                    Development Containers
                </ExternalLink>{" "}
                and{" "}
                <ExternalLink href="https://www.gitpod.io/docs/gitpod/introduction/automations">
                    Automations
                </ExternalLink>{" "}
                for you?
            </Text>
            <Text className="text-base">
                It can take as little as five minutes for smaller projects, but for larger ones it can take up to one
                hour. I&apos;ll ask your permission before committing or pushing changes. While I&apos;m working you can
                safely leave this page and come back later.
            </Text>
        </>
    );
};
